import useLocalization from "@/hooks/useLocalization";
import useUser from "@/hooks/useUser";
import useVenue from "@/hooks/useVenue";
import { Alert, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardFilters from "./components/DashboardFilters";
import DashboardMetricsTable from "./components/DashboardMetricsTable";
import {
  DashboardPageDataSkeleton,
  DashboardPageSkeleton,
} from "./components/DashboardSkeletons";
import GuestsOverTimeChart from "./components/GuestsOverTimeChart";
import MetricsSummaryGrid from "./components/MetricsSummaryGrid";
import ReservationRequestsOverTimeChart from "./components/ReservationRequestsOverTimeChart";
import RevenueOverTimeChart from "./components/RevenueOverTimeChart";
import { DEFAULT_GRAPH_SCOPE, DEFAULT_VIEW_MODE } from "./constants";
import useDashboardMetrics from "./useDashboardMetrics";

export default function DashboardPage() {
  const { t } = useLocalization();
  const { isMtUser, user: { userId } } = useUser();
  const navigate = useNavigate();
  const { loading: venuesLoading, productMap, venueMap, venues } = useVenue();

  useEffect(() => {
    if (!isMtUser) navigate("/", { replace: true });
  }, [isMtUser, navigate]);
  const [venueId, setVenueId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [graphScope, setGraphScope] = useState(DEFAULT_GRAPH_SCOPE);
  const [viewMode, setViewMode] = useState(DEFAULT_VIEW_MODE);
  const { error, hasData, loading, metrics, rollingMetrics, rollingWindowDays } =
    useDashboardMetrics({
      graphScope,
      productId: productId ?? undefined,
      venueId: venueId ?? undefined,
    });
  const productOptions = useMemo(() => {
    if (venueId) return venueMap.get(venueId)?.products ?? [];

    return Array.from(productMap.values());
  }, [productMap, venueId, venueMap]);

  useEffect(() => {
    if (!venueId) return;
    if (venues.some((venue) => venue.id === venueId)) return;

    setVenueId(null);
  }, [venueId, venues]);

  useEffect(() => {
    if (!productId) return;
    if (productOptions.some((product) => product.id === productId)) return;

    setProductId(null);
  }, [productId, productOptions]);

  useEffect(() => {
    setProductId(null);
    setVenueId(null);
    setGraphScope(DEFAULT_GRAPH_SCOPE);
    setViewMode(DEFAULT_VIEW_MODE);
  }, [userId]);

  if (venuesLoading) return <DashboardPageSkeleton includeFilters />;

  return (
    <Stack gap={3} sx={{ padding: 2 }}>
      <DashboardFilters
        graphScope={graphScope}
        onGraphScopeChange={setGraphScope}
        onProductChange={setProductId}
        onVenueChange={setVenueId}
        onViewModeChange={setViewMode}
        productId={productId}
        products={productOptions}
        venueId={venueId}
        venues={venues}
        viewMode={viewMode}
      />

      {loading && <DashboardPageDataSkeleton />}

      {error && !loading && <Alert severity="error">{error.message}</Alert>}

      {!loading && !error && !hasData && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            {t("dashboard:noData")}
          </Typography>
        </Paper>
      )}

      {!loading && !error && hasData && (
        <>
          <MetricsSummaryGrid
            rollingMetrics={rollingMetrics}
            rollingWindowDays={rollingWindowDays}
          />

          {viewMode === "table" ? (
            <DashboardMetricsTable graphScope={graphScope} metrics={metrics} />
          ) : (
            <>
              <ReservationRequestsOverTimeChart
                graphScope={graphScope}
                metrics={metrics}
              />

              <GuestsOverTimeChart graphScope={graphScope} metrics={metrics} />

              <RevenueOverTimeChart graphScope={graphScope} metrics={metrics} />
            </>
          )}
        </>
      )}
    </Stack>
  );
}
